/* Use the IP country first, then an existing shipping destination. Checkout keeps its own address rules. */
(function () {
  if (window.DJ_SHIPPING_PROMOTION) return;

  const copyNode = document.querySelector('[data-dj-shipping-copy]');
  if (!copyNode) return;
  let copy;
  try { copy = JSON.parse(copyNode.textContent); } catch (error) { return; }

  const HU_THRESHOLD = 1499000;
  const ABROAD_THRESHOLD = 2499000;
  const DESTINATIONS = ['HU', 'RO', 'SK'];
  const DESTINATION_KEY = 'dj_shipping_destination';
  const selector = '[data-dj-shipping-label], [data-dj-shipping-cart], [data-dj-shipping-builder], [data-dj-shipping-destination]';
  let state = Object.freeze({ country: null, threshold: null, status: 'pending', source: null });
  let ipCountry = null;
  let cartCountry = null;
  let cartReadRevision = 0;
  let destinationChanged = false;
  let selectedDestination = null;
  let detectionComplete = false;
  let scheduled = false;

  function countryCode(value) {
    return typeof value === 'string' && /^[A-Za-z]{2}$/.test(value) ? value.toUpperCase() : null;
  }

  try {
    const stored = sessionStorage.getItem(DESTINATION_KEY);
    if (DESTINATIONS.includes(stored)) selectedDestination = stored;
  } catch (error) {}

  function enteredDestination() {
    let saved;
    let zip;
    try {
      saved = JSON.parse(sessionStorage.getItem('dj_cp_state') || '{}');
      zip = (sessionStorage.getItem('dj_cp_zip') || '').trim();
    } catch (error) { return null; }
    if (!saved || typeof saved !== 'object') return null;
    const point = saved && saved.point;
    if (saved.mode === 'pont' && point && ['id', 'addr', 'city', 'zip'].every(function (key) { return String(point[key] || '').trim(); })) {
      const country = countryCode(point.ctry);
      if (DESTINATIONS.includes(country)) return { country: country, source: 'pickup-address' };
    }
    if (saved.mode === 'haz' && zip) {
      if (/^\d{4}$/.test(zip)) return { country: 'HU', source: 'home-address' };
      const country = countryCode(saved.ctry);
      if (country && country !== 'HU' && DESTINATIONS.includes(country) && zip.replace(/\s/g, '').length >= 3) {
        return { country: country, source: 'home-address' };
      }
    }
    return null;
  }

  function resolveState() {
    if (!detectionComplete) return;
    const entered = enteredDestination();
    let choice = ipCountry ? { country: ipCountry, source: 'ip' } : null;
    // A newly chosen point/address supersedes the older cart address snapshot.
    if (!choice && destinationChanged && entered) choice = entered;
    if (!choice && cartCountry) choice = { country: cartCountry, source: 'cart-address' };
    if (!choice && entered) choice = entered;
    if (!choice && selectedDestination) choice = { country: selectedDestination, source: 'destination-selection' };
    state = Object.freeze({
      country: choice ? choice.country : null,
      threshold: choice ? (choice.country === 'HU' ? HU_THRESHOLD : ABROAD_THRESHOLD) : null,
      status: choice ? 'resolved' : 'destination-required',
      source: choice ? choice.source : null,
    });
  }

  function validAmount(amount) {
    return Number.isFinite(Number(amount)) && Number(amount) >= 0;
  }

  function label() {
    return state.country ? (state.country === 'HU' ? copy.hu : copy.abroad) : copy.unknown;
  }

  function progressFor(amount) {
    if (!state.threshold || !validAmount(amount)) return { known: false, eligible: false, percent: 0 };
    return {
      known: true,
      eligible: Number(amount) >= state.threshold,
      percent: Math.min(100, Number(amount) / state.threshold * 100),
    };
  }

  function messageFor(amount) {
    const progress = progressFor(amount);
    if (!progress.known) return label();
    const suffix = state.country === 'HU' ? 'Hu' : 'Abroad';
    if (progress.eligible) return copy['achieved' + suffix];
    const remaining = ((state.threshold - Number(amount)) / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' Ft';
    return copy['remaining' + suffix].replace('__AMOUNT__', remaining);
  }

  function setText(node, text) {
    if (node && node.textContent !== text) node.textContent = text;
  }

  function refresh() {
    resolveState();
    for (const node of document.querySelectorAll('[data-dj-shipping-label]')) setText(node, label());
    for (const node of document.querySelectorAll('[data-dj-shipping-destination]')) {
      node.hidden = !detectionComplete || !!(state.country && state.source !== 'destination-selection');
      const select = node.querySelector('[data-dj-shipping-destination-country]');
      if (select) select.value = selectedDestination || '';
    }
    for (const node of document.querySelectorAll('[data-dj-shipping-cart]')) {
      const result = progressFor(node.dataset.subtotal);
      setText(node.querySelector('[data-dj-shipping-message]'), messageFor(node.dataset.subtotal));
      node.classList.toggle('is-ok', result.eligible);
      const bar = node.querySelector('[data-dj-shipping-progress]');
      if (bar) {
        bar.hidden = !result.known;
        bar.setAttribute('aria-valuenow', String(Math.round(result.percent)));
        const fill = bar.querySelector('i');
        if (fill) fill.style.width = result.percent.toFixed(1) + '%';
      }
    }
    document.dispatchEvent(new CustomEvent('dj:shipping-country', { detail: state }));
  }

  function scheduleRefresh() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () { scheduled = false; refresh(); });
  }

  async function requestJson(url, options) {
    const controller = new AbortController();
    const timeout = setTimeout(function () { controller.abort(); }, 8000);
    try {
      const response = await fetch(url, Object.assign({ credentials: 'same-origin', cache: 'no-store' }, options || {}, { signal: controller.signal }));
      if (!response.ok) throw new Error('Shipping country unavailable');
      return await response.json();
    } finally { clearTimeout(timeout); }
  }

  async function selectedCartCountry() {
    try {
      const cookie = document.cookie.split('; ').find(function (entry) { return entry.indexOf('cart=') === 0; });
      let token = cookie ? decodeURIComponent(cookie.slice(5)) : '';
      if (token.indexOf('?key=') < 0) {
        const cart = await requestJson('/cart.js');
        token = cart.token || '';
      }
      if (typeof token !== 'string' || token.indexOf('?key=') < 0) return null;
      const data = await requestJson('/api/2026-10/graphql.json', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: 'query($id:ID!){cart(id:$id){delivery{addresses{selected address{... on CartDeliveryAddress{countryCode}}}}}}',
          variables: { id: 'gid://shopify/Cart/' + token },
        }),
      });
      if (data.errors) return null;
      const addresses = data.data && data.data.cart && data.data.cart.delivery && data.data.cart.delivery.addresses;
      if (!Array.isArray(addresses)) return null;
      const selected = addresses.filter(function (entry) { return entry.selected; });
      return selected.length === 1 ? countryCode(selected[0].address && selected[0].address.countryCode) : null;
    } catch (error) { return null; }
  }

  async function detectCountry() {
    try {
      const data = await requestJson('/browsing_context_suggestions.json?country[enabled]=true');
      const detected = data && data.detected_values && data.detected_values.country && data.detected_values.country.handle;
      ipCountry = countryCode(detected);
    } catch (error) {}
    if (!ipCountry) await refreshCartCountry();
    detectionComplete = true;
    refresh();
    return state;
  }

  async function refreshCartCountry() {
    const revision = ++cartReadRevision;
    const country = await selectedCartCountry();
    if (revision !== cartReadRevision) return false;
    cartCountry = country;
    destinationChanged = false;
    return true;
  }

  // No persistent country cache: a new page visit must reflect its current IP country.
  window.DJ_SHIPPING_PROMOTION = Object.freeze({
    getState: function () { return state; }, messageFor: messageFor, progressFor: progressFor,
    ready: detectCountry(),
  });
  refresh();

  window.addEventListener('pageshow', async function (event) {
    if (!event.persisted || ipCountry) return;
    if (await refreshCartCountry()) refresh();
  });

  document.addEventListener('change', function (event) {
    const select = event.target.closest('[data-dj-shipping-destination-country]');
    if (select) {
      selectedDestination = DESTINATIONS.includes(select.value) ? select.value : null;
      try {
        if (selectedDestination) sessionStorage.setItem(DESTINATION_KEY, selectedDestination);
        else sessionStorage.removeItem(DESTINATION_KEY);
      } catch (error) {}
      refresh();
    } else if (event.target.closest('[data-dj-cp]')) {
      destinationChanged = true; scheduleRefresh();
    }
  });
  document.addEventListener('input', function (event) {
    if (event.target.closest('[data-dj-zip]')) { destinationChanged = true; scheduleRefresh(); }
  });
  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-dj-cp]')) { destinationChanged = true; scheduleRefresh(); }
  });

  // Section rendering can replace promo nodes or update the cart subtotal without a page reload.
  const observer = new MutationObserver(function (records) {
    for (const record of records) {
      if (record.type === 'attributes') { scheduleRefresh(); return; }
      for (const node of record.addedNodes) {
        if (node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector))) {
          scheduleRefresh(); return;
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-subtotal'] });
})();
